<template>
  <div class="edit-store-container">
    <el-card class="form-card">
      <div slot="header" class="form-header">
        <span><i class="el-icon-edit"></i> 编辑店铺信息</span>
        <el-button type="text" @click="goBack">返回</el-button>
      </div>

      <el-form
          ref="storeForm"
          :model="formData"
          :rules="formRules"
          label-width="120px"
          class="store-form"
      >

        <!-- 店铺基本信息 -->
        <div class="form-section">
          <h3>店铺基本信息</h3>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="店铺名称" prop="name" required>
                <el-input v-model="formData.name" placeholder="请输入店铺名称" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="店铺分类" prop="categoryId" required>
                <div class="category-selector">
                  <el-input
                      v-model="selectedCategoryPath"
                      placeholder="请选择店铺分类"
                      readonly
                      suffix-icon="el-icon-arrow-down"
                      @focus="showCategoryDialog = true"
                  />
                  <el-button
                      v-if="formData.productCategoryId || formData.categoryId || formData.industryId"
                      type="text"
                      class="clear-category-btn"
                      @click="clearCategory"
                  >清除</el-button>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="联系电话" prop="contactPhone" required>
                <el-input v-model="formData.contactPhone" placeholder="请输入联系电话" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="健康积金">
                <el-switch
                    v-model="healthFundEnabled"
                    active-text="开启"
                    inactive-text="关闭"
                    active-color="#13ce66"
                    inactive-color="#dcdfe6"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="店铺地址" prop="address" required>
            <el-input v-model="formData.address" type="textarea" :rows="2" placeholder="请输入详细地址" />
          </el-form-item>
        </div>

        <!-- 提交按钮 -->
        <div class="form-actions">
          <el-button @click="goBack">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">保存修改</el-button>
        </div>
      </el-form>
    </el-card>

    <!-- 店铺分类选择弹窗 -->
    <el-dialog
        title="选择店铺分类"
        :visible.sync="showCategoryDialog"
        width="700px"
        :close-on-click-modal="false"
        append-to-body
        @closed="resetTempCategory"
    >
      <div class="category-dialog-content">
        <div class="category-tree-container">
          <el-tree
              ref="categoryTree"
              :data="categoryList"
              :props="{ children: 'children', label: 'name' }"
              :expand-on-click-node="false"
              node-key="id"
              :default-expanded-keys="defaultExpandedKeys"
              highlight-current
              @node-click="handleCategoryNodeClick"
          >
            <template #default="{ node, data }">
              <div class="category-tree-node">
                <span class="node-name">{{ data.name }}</span>
                <span class="node-count" v-if="data.merchantCount !== undefined">({{ data.merchantCount }})</span>
                <el-tag size="mini" :type="data.level === 1 ? '' : data.level === 2 ? 'warning' : 'success'" class="node-tag">
                  {{ data.level === 1 ? '一级' : data.level === 2 ? '二级' : '三级' }}
                </el-tag>
              </div>
            </template>
          </el-tree>
        </div>

        <div class="category-tip">
          <i class="el-icon-info"></i>
          选择任意级别的分类，系统会自动填充对应的一级、二级、三级分类ID
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <span class="selected-info" v-if="tempSelectedCategory">
          已选择：{{ getCategoryFullPath(tempSelectedCategory) }}
        </span>
        <el-button @click="showCategoryDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmCategory" :disabled="!tempSelectedCategory">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from "@/utils/api";

export default {
  name: 'EditStore',
  data() {
    return {
      submitting: false,
      storeId: null,
      formData: {
        name: '',
        contactPhone: '',
        address: '',
        industryId: null,
        categoryId: null,
        productCategoryId: null,
        categoryPath: '',
        merchantLevel: 1
      },
      formRules: {
        name: [
          { required: true, message: '请输入店铺名称', trigger: 'blur' },
          { max: 100, message: '店铺名称不能超过100个字符', trigger: 'blur' }
        ],
        categoryId: [
          { required: true, message: '请选择店铺分类', trigger: 'change' }
        ],
        contactPhone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' }
        ],
        address: [
          { required: true, message: '请输入店铺地址', trigger: 'blur' }
        ]
      },

      showCategoryDialog: false,
      categoryList: [],
      tempSelectedCategory: null,
      defaultExpandedKeys: []
    };
  },

  computed: {
    selectedCategoryPath() {
      if (this.tempSelectedCategory) {
        return this.getCategoryFullPath(this.tempSelectedCategory);
      }
      if (this.formData.categoryPath) {
        return this.formData.categoryPath;
      }
      return '';
    },

    healthFundEnabled: {
      get() {
        return this.formData.merchantLevel === 3;
      },
      set(val) {
        this.formData.merchantLevel = val ? 3 : 1;
      }
    }
  },

  methods: {
    async loadCategoryList() {
      try {
        const res = await api.merchantCategory_getMerchantCategories({ countMerchant: false });
        if (res?.data?.code === '000' && res.data.success) {
          this.categoryList = res.data.data || [];
          if (this.categoryList.length > 0) {
            this.defaultExpandedKeys = this.categoryList.map(item => item.id);
          }
        }
      } catch (err) {
        console.error('加载分类列表失败：', err);
      }
    },

    loadStoreInfo() {
      this.storeId = this.$route.query.id || this.$route.params.id;
      if (!this.storeId) {
        this.$message.error('缺少店铺ID参数');
        this.goBack();
        return;
      }

      const loading = this.$loading({ text: '加载店铺信息...' });

      api.merchant_getMerchantDetail({ id: this.storeId }).then(res => {
        loading.close();
        if (res?.data?.code === '000' && res.data.success) {
          const data = res.data.data;
          this.formData = {
            name: data.name || '',
            contactPhone: data.contactPhone || '',
            address: data.address || '',
            industryId: data.industryId || null,
            categoryId: data.categoryId || null,
            productCategoryId: data.productCategoryId || null,
            categoryPath: '',
            merchantLevel: data.merchantLevel || 1
          };
          this.matchCategoryPath();
        } else {
          this.$message.error(res?.data?.message || '获取店铺信息失败');
          this.goBack();
        }
      }).catch(err => {
        loading.close();
        console.error('加载店铺信息失败：', err);
        this.$message.error('加载店铺信息失败');
        this.goBack();
      });
    },

    matchCategoryPath() {
      if (!this.formData.industryId || this.categoryList.length === 0) return;

      const findPath = (list, targetId, path = []) => {
        for (let item of list) {
          const currentPath = [...path, item];
          if (item.id === targetId) return currentPath;
          if (item.children && item.children.length) {
            const found = findPath(item.children, targetId, currentPath);
            if (found) return found;
          }
        }
        return null;
      };

      const targetId = this.formData.productCategoryId
          || this.formData.categoryId
          || this.formData.industryId;

      if (targetId) {
        const path = findPath(this.categoryList, targetId);
        if (path) {
          this.formData.categoryPath = path.map(item => item.name).join(' / ');
        }
      }
    },

    goBack() {
      this.$router.go(-1);
    },

    handleCategoryNodeClick(data) {
      const findPath = (list, targetId, path = []) => {
        for (let item of list) {
          const currentPath = [...path, item];
          if (item.id === targetId) return currentPath;
          if (item.children && item.children.length) {
            const found = findPath(item.children, targetId, currentPath);
            if (found) return found;
          }
        }
        return null;
      };

      const fullPath = findPath(this.categoryList, data.id);

      const pathIds = {
        industryId: null,
        categoryId: null,
        productCategoryId: null
      };

      if (fullPath) {
        if (fullPath.length >= 1) pathIds.industryId = fullPath[0].id;
        if (fullPath.length >= 2) pathIds.categoryId = fullPath[1].id;
        if (fullPath.length >= 3) pathIds.productCategoryId = fullPath[2].id;
      }

      this.tempSelectedCategory = {
        id: data.id,
        name: data.name,
        level: data.level,
        path: fullPath,
        industryId: pathIds.industryId,
        categoryId: pathIds.categoryId,
        productCategoryId: pathIds.productCategoryId
      };
    },

    getCategoryFullPath(category) {
      if (!category || !category.path) return '';
      return category.path.map(item => item.name).join(' / ');
    },

    confirmCategory() {
      if (!this.tempSelectedCategory) {
        this.$message.warning('请选择分类');
        return;
      }

      this.formData.industryId = this.tempSelectedCategory.industryId;
      this.formData.categoryId = this.tempSelectedCategory.categoryId;
      this.formData.productCategoryId = this.tempSelectedCategory.productCategoryId;
      this.formData.categoryPath = this.getCategoryFullPath(this.tempSelectedCategory);
      this.showCategoryDialog = false;
      this.$message.success(`已选择：${this.formData.categoryPath}`);
    },

    clearCategory() {
      this.formData.industryId = null;
      this.formData.categoryId = null;
      this.formData.productCategoryId = null;
      this.formData.categoryPath = '';
      this.tempSelectedCategory = null;
    },

    resetTempCategory() {
      this.tempSelectedCategory = null;
    },

    async submitForm() {
      try {
        await this.$refs.storeForm.validate();

        await this.$confirm('确认要保存修改吗？', '确认修改', {
          confirmButtonText: '确认保存',
          cancelButtonText: '取消',
          type: 'warning'
        });

        this.submitting = true;

        const response = await api.merchant_updateMerchant({
          id: this.storeId,
          merchant: JSON.stringify({
            name: this.formData.name,
            contactPhone: this.formData.contactPhone,
            address: this.formData.address,
            industryId: this.formData.industryId,
            categoryId: this.formData.categoryId,
            productCategoryId: this.formData.productCategoryId,
            merchantLevel: this.formData.merchantLevel
          })
        });

        const responseData = response.data || response;

        if (responseData.code === "000") {
          this.$message.success('店铺信息修改成功！');
          setTimeout(() => {
            this.goBack();
          }, 1500);
        } else {
          this.$message.error(responseData.message || '修改失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('提交失败:', error);
          this.$message.error(error.message || '提交失败，请重试');
        }
      } finally {
        this.submitting = false;
      }
    }
  },

  async mounted() {
    await this.loadCategoryList();
    this.loadStoreInfo();
  }
};
</script>

<style scoped>
.edit-store-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.form-card {
  max-width: 800px;
  margin: 0 auto;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
}

.form-header i {
  margin-right: 8px;
  color: #409eff;
}

.store-form {
  padding: 0 20px 20px;
}

.form-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}

.form-section h3 {
  margin-bottom: 20px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.form-actions {
  text-align: center;
  padding: 20px 0 10px;
}

.form-actions .el-button {
  min-width: 100px;
  margin: 0 10px;
}

.category-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-selector .el-input {
  flex: 1;
}

.clear-category-btn {
  font-size: 12px;
  color: #f56c6c;
  flex-shrink: 0;
}

.clear-category-btn:hover {
  color: #f78989;
}

.category-dialog-content {
  min-height: 300px;
}

.category-tree-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px;
  background: #fafbfc;
}

.category-tree-node {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.category-tree-node .node-name {
  font-size: 14px;
  color: #303133;
}

.category-tree-node .node-count {
  font-size: 12px;
  color: #909399;
}

.category-tree-node .node-tag {
  margin-left: auto;
}

.category-tip {
  margin-top: 16px;
  padding: 10px 16px;
  background: #f0f9ff;
  border-radius: 6px;
  font-size: 13px;
  color: #409eff;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.dialog-footer .selected-info {
  flex: 1;
  font-size: 13px;
  color: #67c23a;
}

::v-deep .el-tree-node__content {
  height: 36px;
}

::v-deep .el-tree-node.is-current > .el-tree-node__content {
  background-color: #ecf5ff;
  border-radius: 4px;
}
</style>